import React, { useState, useEffect, useRef, useCallback } from 'react';
import { sendRequest, API_ROOT } from '../supplementary_components/requests';
import { addNotification } from '../supplementary_components/store';
import { LoadingIcon } from '../supplementary_components/loading_icon';

import { PopupWindow } from './teamCard/truckCard/truckCardComponents/popupWindow';
import { WebSocketController } from './websocketController';
// import { MainBoardSearchBox } from './mainBoardSearch';
import { UnassignedLoadsTable } from './unassignedLoadsTable';
import { MainBoardNotes } from './mainBoardNotes';
import styles from './mainBoardSettings.module.scss';
import { TeamContentWrapper } from './teamsContentWrapper';
import { InputWithOptions } from '../general_components/content_management/reusableItems/inputWithOptions';
import { useBoardStore } from './boardStore';
import { MainBoardSearchBox } from './mainBoardSearch';
import { MainBoardStatistics } from './mainBoardStatistics';
import { IosToggle } from '../general_components/content_management/reusableItems/iosToggle';
import { retrieveCompanyData } from '../supplementary_components/companySettings';
import {
    retrieveMainBoardCustomizations,
    can,
    updateLocalMainBoardCustomizations,
} from '../supplementary_components/userDataDelegate';
import {
    buildPlannerBoardConf,
    buildPlannerColumnAccessOpts,
    buildRegularBoardConf,
    columnsForBoard,
} from './plannerBoardSettings';

export const MainBoard = ({userCanSeeLoadRates})=> {
    const teams = useBoardStore(s => s.teams);
    const currentTime = useBoardStore(s => s.currentTime);
    const clearBoard = useBoardStore(s => s.clearBoard);
    const hydrateFromSnapshot = useBoardStore(s => s.hydrateFromSnapshot);
    const hydratePlannerUiSettings = useBoardStore(s => s.hydratePlannerUiSettings);
    const plannerColumns = useBoardStore(s => s.plannerColumns);
    const plannerUiSettingsVersion = useBoardStore(s => s.plannerUiSettingsVersion);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const companyData = retrieveCompanyData();
    const currentUserId = parseInt(localStorage.getItem("currentUserId"));
    const currentCompanyId = parseInt(companyData.id);
    const mainlySolo = companyData.mainlyOperatesWithSolo === true || companyData.mainlyOperatesWithSolo === "true";
    const mainBoardCustomizations = retrieveMainBoardCustomizations();
    const plannerViewModeState = mainBoardCustomizations.plannerViewMode;
    const [plannerMode, setPlannerMode] = useState(plannerViewModeState !== false);
    const storedTeamNames = mainBoardCustomizations.selectedTeamsList[String(currentCompanyId)] || [];
    const [selectedTeamNames, setSelectedTeams] = useState(storedTeamNames);
    const [teamOptionsLoading, setTeamOptionsLoading] = useState(false);
    const [teamsRefreshing, setTeamsRefreshing] = useState(false);
    
    const popupViews = useRef({});
    const popupViewManagingObject = useCallback((id, ref, setState) => {
        const map = popupViews.current;
        if(!map[id]){
            map[id] = {
                ref : ref.current,
                setState: setState
            }
        }
        // eslint-disable-next-line 
    }, []);

    useEffect(() => {
        hydratePlannerUiSettings();
        const timer = setInterval(() => {
            useBoardStore.getState().bumpStatusClock?.();
        }, 60 * 60 * 1000);
        return () => clearInterval(timer);
    // eslint-disable-next-line
    }, []);
    
    useEffect(() => {
        
        let ignore = false;
        
        const controller = new AbortController();
        async function fetchTeams() {
            if (!ignore) {
                const signal = controller.signal;
                setTeamsRefreshing(true);
                updateLocalMainBoardCustomizations({
                    companyId: currentCompanyId,
                    plannerViewMode: plannerMode,
                    selectedTeamsForCompany: selectedTeamNames,
                });
                sendRequest("GET", API_ROOT+"teams/teamsForMainBoard?plannerMode="+plannerMode+"&teamNames="+selectedTeamNames.join(','), signal, (val)=>{
                    val.teams.forEach(function(team,i){
                        if(team.membersIds.includes(currentUserId)){
                            val.teams.splice(i, 1);
                            val.teams.unshift(team);
                        }
                    });
                    hydrateFromSnapshot(val, plannerMode);
                    setLoading(false);
                    setTeamsRefreshing(false);
                    setError(false);
                    
                }, ()=>{
                    setError(true);
                    setLoading(false);
                    setTeamsRefreshing(false);
                    addNotification({message:`Something went wrong`, type:'alert'})
                })
            }
        }
        fetchTeams();
        function handleClickOutside (event){            
            const map = popupViews.current;
            for (const key of Object.keys(map)) {
                if(map[key].ref && !map[key].ref.contains(event.target) ){
                    map[key].setState(false);
                    delete map[key];
                }
            }
        } 
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            ignore = true;
            controller.abort();
            setTeamsRefreshing(false);
            clearBoard();
            document.removeEventListener("mousedown", handleClickOutside);
            
        };
    // eslint-disable-next-line     
    }, [selectedTeamNames, plannerMode])
    if(loading) return <LoadingIcon state={true} height={'10rem'} />
    if(error) return <div className='errorMessage'>Something went wrong</div>

    const userIsUpdater = !can('DRIVER_PAYOUTS', 'VIEW');
    const columnAccess = buildPlannerColumnAccessOpts({
        userCanSeeRates: userCanSeeLoadRates,
        userIsUpdater,
    });
    const resolvedPlannerColumns = columnsForBoard(plannerColumns, columnAccess);
    // Keep planner conf subscribed to settings version after Save
    void plannerUiSettingsVersion;

    const MAIN_BOARD_CONF = plannerMode
        ? buildPlannerBoardConf(resolvedPlannerColumns)
        : buildRegularBoardConf(userCanSeeLoadRates, resolvedPlannerColumns);
    const boardConf = GenerateHeaderConf(MAIN_BOARD_CONF)
    const ALL_TEAMS_SELECTED = selectedTeamNames.length === 1 && (selectedTeamNames[0] === "All Teams" || selectedTeamNames[0] === "All Trucks");
    const teamSelectionColumnConfig = {
        suggestionsEnabled: true,
        suggestionsLink: API_ROOT+'teams/namecontains?ignoreTeams='+selectedTeamNames.join(',')+'&pattern=',
        onSuggestionsLoadingChange: setTeamOptionsLoading,
        displayValueGenerator: (val)=>{return `${val['teamName']}`},
        customOnSelectMethod : (selectedVal)=>{
            const teamName = selectedVal.teamName;
            if(teamName === "All Teams" || teamName === "All Trucks"){
                setSelectedTeams([teamName]);
            }else{
                setSelectedTeams(prev => [...prev, teamName]);
            }
        }
    }
    return <div style={{marginBottom: '3rem'}}>
        <MainBoardNotes />
        <UnassignedLoadsTable />
        <div className={styles.settingsContainer}>
            <InputWithOptions selectedItemNames={selectedTeamNames} setSelectedItems={setSelectedTeams}  allSelected={ALL_TEAMS_SELECTED} columnConfig={teamSelectionColumnConfig} itemLabel="Team" dataIsLoading={teamOptionsLoading || teamsRefreshing} />
        
            <div className={styles.plannerModeWrapper} >
                <div className={styles.label}>Planner Mode:</div>
                <IosToggle state={plannerMode} onChangeAction={(checked)=>{
                    setPlannerMode(checked);
                    updateLocalMainBoardCustomizations({
                        companyId: currentCompanyId,
                        plannerViewMode: checked,
                    });
                }} />
            </div>
            <MainBoardStatistics userCanSeeLoadRates={userCanSeeLoadRates} />
            <MainBoardSearchBox />
        </div>
        {teams ? <TeamContentWrapper  teamsList={teams} boardConf={boardConf} currentTime={currentTime} plannerMode={plannerMode} popupViewManagingObject={popupViewManagingObject} currentCompanyId={currentCompanyId} mainlySolo={mainlySolo} userCanSeeLoadRates={userCanSeeLoadRates} />
        :null}
        <PopupWindow />
        {teams != null ? <WebSocketController /> : null}
    </div>
}

const GenerateHeaderConf = (savedConf) => {
    let result = savedConf;
    for(let i = 0; i < savedConf.columns.length; i++){
        if(!savedConf.columns[i].hiddenField){
            switch (savedConf.columns[i].name) {
                case "truckNumber":
                    result.columns[i]["label"] = "Truck"
                    break;
                case "truckState":
                    result.columns[i]["label"] = "Sts"
                    break;
                case "drivers":
                    result.columns[i]["label"] = "Driver Name"
                    break;
                case "trailer":
                    result.columns[i]["label"] = "Trailer";
                    break;
                case "status":
                    result.columns[i]["label"] = "Status";
                    break;
                case "truckStatus":
                    result.columns[i]["label"] = "Truck Status";
                    break;
                case "currentLocation":
                    result.columns[i]["label"] = "Location";
                    break;
                case "destination":
                    result.columns[i]["label"] = "Destination";
                    break;
                case "currentLoadNumber":
                    result.columns[i]["label"] = "Load Number";
                    break;
                case "fuelLevel":
                    result.columns[i]["label"] = "Fuel";
                    break;
                case "notes":
                    result.columns[i]["label"] = "Notes";
                    break;
                case "gross":
                    result.columns[i]["label"] = "Gross";
                    break;
                case "rpm":
                    result.columns[i]["label"] = "RPM";
                    break;
                case "payoutGross":
                    result.columns[i]["label"] = "Payout Gross";
                    break;
                case "payoutRpm":
                    result.columns[i]["label"] = "Payout RPM";
                    break;
                default:
                    result.columns[i]["label"] = "Unknown Label";
                    break;
            }
        }
    }
    result.columns.unshift(
        {
            spacing:true
        }
    )
    return result;
}
